/* workspace-admin/Modules — Enterprise module management.
   M-01: disable confirmation dialog (with blocker impact visualization)
   M-02 FE: zero-module guard — Save disabled when enabled.size === 0
   M-03: dependency chip badges replacing plain dep text
   L-05: per-row pending state — only changed rows show spinner during save
   H-01: dep enforcement — auto-enables deps, blocks disabling with dependents
   H-03: cache invalidation — dispatches nx-modules-changed after save
   H-04: reset to defaults button with confirmation */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, ConfirmDialog } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead } from '../platform/_ui';
import { findWorkspace } from '../platform/platformStore';
import * as workspaceApi from '../api/workspace.api.js';
import { invalidateWorkspaceRuntime } from '../workspace/WorkspaceRuntimeContext.jsx';
import { MODULE_CATALOG, MODULE_CATEGORIES, DEFAULT_MODULES } from '../shared/modules.js';

/* ── Toggle switch ──────────────────────────────────────────────────────────── */
function Toggle({ on, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      style={{
        position: 'relative', display: 'inline-block', flexShrink: 0,
        width: 40, height: 22, borderRadius: 11, padding: 0, border: 'none',
        background: on ? 'var(--nx-indigo-600, #4f46e5)' : 'var(--nx-border, #cbd5e1)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 0.15s',
      }}
    >
      <span style={{
        position: 'absolute', top: 2, width: 18, height: 18, borderRadius: '50%',
        left: on ? 20 : 2, background: '#fff', transition: 'left 0.15s',
        boxShadow: '0 1px 3px rgba(0,0,0,.2)',
      }} />
    </button>
  );
}

/* ── Dep helpers (pure functions) ────────────────────────────────────────────── */
function getDepsToAdd(moduleId, currentEnabled) {
  const mod = MODULE_CATALOG.find((m) => m.id === moduleId);
  if (!mod) return [];
  return mod.deps.filter((dep) => !currentEnabled.has(dep));
}

function getBlockers(moduleId, currentEnabled) {
  return MODULE_CATALOG
    .filter((m) => currentEnabled.has(m.id) && m.deps.includes(moduleId))
    .map((m) => m.label);
}

/* ── M-01: Disable confirmation / blocker info dialog ───────────────────────── */
function DisableDialogPanel({ dialog, onConfirm, onCancel }) {
  const { label, blockers } = dialog;
  const canDisable = blockers.length === 0;
  const iconName  = canDisable ? 'AlertCircle' : 'Lock';
  const iconColor = canDisable ? 'var(--nx-danger)' : 'var(--nx-amber-600, #d97706)';
  const iconBg    = canDisable ? 'var(--nx-danger-tint, #fef2f2)' : 'var(--nx-amber-50, #fffbeb)';
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <button type="button" aria-label="Close" onClick={onCancel}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.42)', border: 'none', cursor: 'default', width: '100%', height: '100%' }} />
      <div style={{ position: 'relative', background: 'var(--nx-bg)', borderRadius: 12, maxWidth: 420, width: '100%', padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.22)', margin: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <NexusIcon name={iconName} size={16} style={{ color: iconColor }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {canDisable ? `Disable ${label}?` : `Cannot disable ${label}`}
            </div>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: 4, borderRadius: 4, flexShrink: 0 }}>
            <NexusIcon name="X" size={16} />
          </button>
        </div>
        {canDisable ? (
          <p style={{ fontSize: 13, color: 'var(--nx-text)', lineHeight: 1.6, marginBottom: 20 }}>
            Disabling <strong>{label}</strong> removes it from the CRM sidebar immediately.{' '}
            All workspace members will lose access to this module until it is re-enabled.
          </p>
        ) : (
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 13, marginBottom: 10 }}>
              <strong>{label}</strong> cannot be disabled — the following enabled modules depend on it:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {blockers.map((bl) => (
                <span key={bl} style={{
                  display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 5,
                  background: 'var(--nx-amber-50, #fffbeb)', color: 'var(--nx-amber-700, #b45309)',
                  fontSize: 12, fontWeight: 500, border: '1px solid var(--nx-amber-200, #fde68a)',
                }}>{bl}</span>
              ))}
            </div>
            <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
              Disable those modules first, then you can disable <strong>{label}</strong>.
            </p>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="ghost" onClick={onCancel}>{canDisable ? 'Cancel' : 'Understood'}</Btn>
          {canDisable && <Btn variant="danger" onClick={onConfirm}>Disable Module</Btn>}
        </div>
      </div>
    </div>
  );
}

/* ── M-03: Dep chip badge ────────────────────────────────────────────────────── */
function DepChip({ label, tone }) {
  const s = tone === 'amber'
    ? { bg: 'var(--nx-amber-50, #fffbeb)', color: 'var(--nx-amber-700, #b45309)', border: 'var(--nx-amber-200, #fde68a)' }
    : { bg: 'var(--nx-indigo-50, #eef2ff)', color: 'var(--nx-indigo-600, #4f46e5)', border: 'var(--nx-indigo-200, #c7d2fe)' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '1px 7px', borderRadius: 4,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      fontSize: 10, fontWeight: 500,
    }}>{label}</span>
  );
}

/* ── ModuleRow ───────────────────────────────────────────────────────────────── */
function ModuleRow({ mod, idx, itemCount, isOn, isLocked, blockers, isPending, onToggle }) {
  const depLabels = mod.deps.map(
    (depId) => MODULE_CATALOG.find((m) => m.id === depId)?.label ?? depId,
  );
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px',
      borderBottom: idx < itemCount - 1 ? '1px solid var(--nx-border, #e2e8f0)' : 'none',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isOn ? 'var(--nx-indigo-50, #eef2ff)' : 'var(--nx-surface-2, #f1f5f9)',
        color: isOn ? 'var(--nx-indigo-600, #4f46e5)' : 'var(--nx-text-muted, #64748b)',
        transition: 'background 0.15s, color 0.15s',
      }}>
        <NexusIcon name={mod.icon} size={16} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 2 }}>{mod.label}</div>
        <div style={{ fontSize: 12, color: 'var(--nx-text-muted, #64748b)', lineHeight: 1.5 }}>
          {mod.desc}
        </div>
        {/* M-03: "Requires" dep chips */}
        {depLabels.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, flexWrap: 'wrap' }}>
            <NexusIcon name="ArrowRight" size={10} style={{ color: 'var(--nx-text-muted, #94a3b8)', flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: 'var(--nx-text-muted, #94a3b8)', marginRight: 1 }}>Requires</span>
            {depLabels.map((dl) => <DepChip key={dl} label={dl} tone="indigo" />)}
          </div>
        )}
        {/* M-03: "Required by" dep chips */}
        {isOn && blockers.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, flexWrap: 'wrap' }}>
            <NexusIcon name="Lock" size={10} style={{ color: 'var(--nx-amber-500, #f59e0b)', flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: 'var(--nx-amber-600, #d97706)', marginRight: 1 }}>Required by</span>
            {blockers.map((bl) => <DepChip key={bl} label={bl} tone="amber" />)}
          </div>
        )}
      </div>

      {/* L-05: per-row spinner for changed rows during save */}
      {isPending ? (
        <span style={{ width: 40, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <NexusIcon name="Loader" size={15} style={{ color: 'var(--nx-text-muted)', animation: 'nx-spin 1s linear infinite' }} />
        </span>
      ) : (
        <Toggle
          on={isOn}
          disabled={isLocked}
          onChange={() => onToggle(mod.id)}
          label={(isOn ? 'Disable ' : 'Enable ') + mod.label}
        />
      )}
    </div>
  );
}

/* ── Component ──────────────────────────────────────────────────────────────── */
export default function Modules({ data }) {
  const { wsId } = useParams();
  const toast = useToast();

  const [enabled,         setEnabled]         = useState(() => new Set(data.state ? (findWorkspace(data.state, wsId)?.modules ?? []) : []));
  const [saved,           setSaved]           = useState(() => new Set(data.state ? (findWorkspace(data.state, wsId)?.modules ?? []) : []));
  const [search,          setSearch]          = useState('');
  const [collapsed,       setCollapsed]       = useState({});
  const [confirming,      setConfirming]      = useState(false);
  const [resetConfirming, setResetConfirming] = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [pendingIds,      setPendingIds]      = useState(new Set());
  const [disableDialog,   setDisableDialog]   = useState(null);
  const [depNotice,       setDepNotice]       = useState(null);

  const ws       = findWorkspace(data.state, wsId);
  const isLocked = ws ? (ws.status === 'ARCHIVED' || ws.status === 'SUSPENDED') : false;

  /* RC-2 fix: re-sync enabled/saved when platformStore loads after component mounted with stale null state */
  const hasSynced = useRef(false);
  useEffect(() => {
    if (hasSynced.current || !ws?.modules?.length) return;
    hasSynced.current = true;
    setEnabled(prev => prev.size === 0 ? new Set(ws.modules) : prev);
    setSaved(prev => prev.size === 0 ? new Set(ws.modules) : prev);
  }, [data.state, wsId]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasChanges = useMemo(() => {
    if (enabled.size !== saved.size) return true;
    for (const m of enabled) if (!saved.has(m)) return true;
    return false;
  }, [enabled, saved]);

  /* M-02 FE: guard against saving a zero-module workspace */
  const zeroModules = enabled.size === 0;

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () => q
      ? MODULE_CATALOG.filter((m) => m.label.toLowerCase().includes(q) || m.desc.toLowerCase().includes(q))
      : MODULE_CATALOG,
    [q],
  );

  /* M-01: clicking disable opens a dialog instead of an inline transient warning */
  const toggle = useCallback((moduleId) => {
    if (isLocked) return;
    if (enabled.has(moduleId)) {
      const blockerLabels = getBlockers(moduleId, enabled);
      const mod = MODULE_CATALOG.find((m) => m.id === moduleId);
      setDisableDialog({ moduleId, label: mod?.label ?? moduleId, blockers: blockerLabels });
    } else {
      const depsToAdd = getDepsToAdd(moduleId, enabled);
      setEnabled((prev) => {
        const next = new Set(prev);
        next.add(moduleId);
        depsToAdd.forEach((dep) => next.add(dep));
        return next;
      });
      if (depsToAdd.length > 0) {
        const mod = MODULE_CATALOG.find((m) => m.id === moduleId);
        const addedLabels = depsToAdd.map((d) => MODULE_CATALOG.find((m) => m.id === d)?.label ?? d);
        setDepNotice({ label: mod?.label ?? moduleId, addedLabels });
      }
    }
  }, [isLocked, enabled]);

  /* M-01: confirmed disable — applies local state change */
  const confirmDisable = useCallback(() => {
    if (!disableDialog) return;
    setEnabled((prev) => { const next = new Set(prev); next.delete(disableDialog.moduleId); return next; });
    setDisableDialog(null);
  }, [disableDialog]);

  const discard = useCallback(() => { setEnabled(new Set(saved)); setDepNotice(null); }, [saved]);

  const invalidateAndNotify = useCallback(() => {
    invalidateWorkspaceRuntime(wsId);
    window.dispatchEvent(new CustomEvent('nx-modules-changed', { detail: { workspaceId: wsId } }));
  }, [wsId]);

  const applyServerModules = useCallback((modules) => {
    const finalSet = new Set(modules);
    setEnabled(finalSet);
    setSaved(finalSet);
    data.update((s) => ({
      ...s,
      workspaces: s.workspaces.map((w) => w.id === wsId ? { ...w, modules } : w),
    }));
    invalidateAndNotify();
    setDepNotice(null);
  }, [wsId, data, invalidateAndNotify]);

  const handleSave = useCallback(async () => {
    /* L-05: record which specific rows are in-flight */
    const changed = new Set([
      ...[...enabled].filter((m) => !saved.has(m)),
      ...[...saved].filter((m) => !enabled.has(m)),
    ]);
    setPendingIds(changed);
    setSaving(true);
    setConfirming(false);
    try {
      const result = await workspaceApi.updateModules(wsId, [...enabled]);
      applyServerModules(result?.modules ?? [...enabled]);
      toast({ tone: 'success', icon: 'Check', title: 'Modules saved' });
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Save failed';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Save failed', desc: msg });
    } finally {
      setSaving(false);
      setPendingIds(new Set());
    }
  }, [wsId, enabled, saved, applyServerModules, toast]);

  const handleReset = useCallback(async () => {
    setSaving(true);
    setResetConfirming(false);
    try {
      const result = await workspaceApi.resetModules(wsId);
      applyServerModules(result?.modules ?? DEFAULT_MODULES);
      toast({ tone: 'success', icon: 'RotateCcw', title: 'Modules reset to defaults' });
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Reset failed';
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Reset failed', desc: msg });
    } finally {
      setSaving(false);
    }
  }, [wsId, applyServerModules, toast]);

  if (!ws) return null;

  const changesBanner = hasChanges && !isLocked ? (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 16px', marginBottom: 16, borderRadius: 6,
      background: 'var(--nx-indigo-50, #eef2ff)', border: '1px solid var(--nx-indigo-200, #c7d2fe)',
    }}>
      <span style={{ fontSize: 13, color: 'var(--nx-indigo-700, #4338ca)' }}>
        You have unsaved module changes
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn variant="ghost" onClick={discard} disabled={saving}>Discard</Btn>
        <Btn variant="primary" onClick={() => setConfirming(true)} disabled={saving || zeroModules} icon="Save">
          Save Changes
        </Btn>
      </div>
    </div>
  ) : null;

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Modules"
        sub={'Manage CRM modules for ' + ws.name}
        actions={!isLocked && (
          <Btn variant="ghost" icon="RotateCcw" onClick={() => setResetConfirming(true)} disabled={saving}>
            Reset to Defaults
          </Btn>
        )}
      />

      {ws.status === 'SUSPENDED' && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--nx-amber-50, #fffbeb)', border: '1px solid var(--nx-amber-200, #fde68a)', color: 'var(--nx-amber-800, #92400e)' }}>
          <NexusIcon name="PauseCircle" size={16} />
          <span style={{ fontSize: 13 }}>This workspace is <strong>suspended</strong>. Module changes are disabled.</span>
        </div>
      )}
      {ws.status === 'ARCHIVED' && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--nx-surface-2, #f1f5f9)', border: '1px solid var(--nx-border, #e2e8f0)', color: 'var(--nx-text-muted, #64748b)' }}>
          <NexusIcon name="Archive" size={16} />
          <span style={{ fontSize: 13 }}>This workspace is <strong>archived</strong>. Settings are read-only.</span>
        </div>
      )}

      {/* M-02 FE: zero-module guard */}
      {zeroModules && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--nx-danger-tint, #fef2f2)', border: '1px solid var(--nx-danger)', color: 'var(--nx-danger)' }}>
          <NexusIcon name="AlertCircle" size={15} />
          <span style={{ fontSize: 13 }}>A workspace must have at least one module enabled.</span>
        </div>
      )}

      {/* H-01: dep auto-add notice */}
      {depNotice && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--nx-blue-50, #eff6ff)', border: '1px solid var(--nx-blue-200, #bfdbfe)', color: 'var(--nx-blue-700, #1d4ed8)' }}>
          <NexusIcon name="Info" size={15} />
          <span style={{ fontSize: 13 }}>
            Enabling <strong>{depNotice.label}</strong> also requires{' '}
            <strong>{depNotice.addedLabels.join(', ')}</strong> — automatically enabled.
          </span>
          <button type="button" onClick={() => setDepNotice(null)} aria-label="Dismiss"
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 2 }}>
            <NexusIcon name="X" size={13} />
          </button>
        </div>
      )}

      {changesBanner}

      {/* Search */}
      <div style={{ maxWidth: 720, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="Search" size={14} style={{ color: 'var(--nx-text-muted, #64748b)', flexShrink: 0 }} />
          <input
            className="nx-input" style={{ flex: 1 }}
            placeholder="Search modules…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            aria-label="Search modules"
          />
          {search && (
            <button type="button" aria-label="Clear search" onClick={() => setSearch('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted, #64748b)', padding: 4 }}>
              <NexusIcon name="X" size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Category accordions */}
      {MODULE_CATEGORIES.map((cat) => {
        const items = filtered.filter((m) => m.category === cat);
        if (!items.length) return null;
        const isCollapsed = !!collapsed[cat];
        const enabledCount = items.filter((m) => enabled.has(m.id)).length;
        return (
          <div key={cat} className="nx-panel" style={{ marginBottom: 12, maxWidth: 720, overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => setCollapsed((prev) => ({ ...prev, [cat]: !prev[cat] }))}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: '12px 16px', background: 'none', border: 'none',
                borderBottom: isCollapsed ? 'none' : '1px solid var(--nx-border, #e2e8f0)',
                cursor: 'pointer', textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{cat}</span>
                <span style={{ fontSize: 11, color: 'var(--nx-text-muted, #64748b)', fontVariantNumeric: 'tabular-nums' }}>
                  {enabledCount}/{items.length} enabled
                </span>
              </div>
              <NexusIcon name={isCollapsed ? 'ChevronRight' : 'ChevronDown'} size={14} />
            </button>

            {!isCollapsed && items.map((mod, idx) => {
              const isOn     = enabled.has(mod.id);
              const blockers = isOn ? getBlockers(mod.id, enabled) : [];
              return (
                <ModuleRow
                  key={mod.id}
                  mod={mod}
                  idx={idx}
                  itemCount={items.length}
                  isOn={isOn}
                  isLocked={isLocked}
                  blockers={blockers}
                  isPending={pendingIds.has(mod.id)}
                  onToggle={toggle}
                />
              );
            })}
          </div>
        );
      })}

      {filtered.length === 0 && (
        <div className="nx-empty" style={{ maxWidth: 720 }}>
          <NexusIcon name="Search" size={22} />
          <span>No modules match &ldquo;{search}&rdquo;</span>
        </div>
      )}

      {changesBanner}

      {/* M-01: disable confirmation / blocker info */}
      {disableDialog && (
        <DisableDialogPanel
          dialog={disableDialog}
          onConfirm={confirmDisable}
          onCancel={() => setDisableDialog(null)}
        />
      )}

      {/* Save confirmation */}
      {confirming && (
        <ConfirmDialog
          title="Save module changes?"
          desc="Disabling a module removes it from the CRM sidebar and blocks that route for all workspace members immediately."
          confirmLabel="Save Changes"
          busy={saving}
          onConfirm={handleSave}
          onCancel={() => setConfirming(false)}
        />
      )}

      {/* H-04: reset confirmation */}
      {resetConfirming && (
        <ConfirmDialog
          title="Reset to default modules?"
          desc="This restores the default module set — all other modules will be disabled immediately."
          confirmLabel="Reset Modules"
          busy={saving}
          onConfirm={handleReset}
          onCancel={() => setResetConfirming(false)}
        />
      )}
    </div>
  );
}
